import { FC, useMemo } from "react";

import { OutcomeGroupBox } from "./OutcomeGroupBox";
import { ProgressOverviewBox } from "./ProgressOverviewBox";

import { serverGetProgressOverviewData } from "api/api-server-data";
import { useAIContext } from "components/ai/hooks/use-ai-context";
import { t } from "i18n/i18n";
import { usePromiseEffect } from "store/store-hooks";

const STUDENT_AI_PROMPT = `Take the following as context for my subsequent prompts.
As you can see in the attached assessment at the bottom of this context,
I have earned proficiency scores on specific learning outcomes across a range of courses and
work products. Please do not mention this data in your responses. Here are my academic outcomes so far:`;

export const ProgressOverview: FC<{ withOutcomeCards?: boolean }> = ({
  withOutcomeCards = false,
}) => {
  const { data, isLoading, error } = usePromiseEffect(async () => {
    return serverGetProgressOverviewData();
  }, []);

  const context = useMemo(
    () => [STUDENT_AI_PROMPT, JSON.stringify(data?.outcomeGroups)].join("\n\n"),
    [data],
  );

  useAIContext("course_progress_demo", context, "user");

  const outcomeCards = useMemo(
    () =>
      data?.outcomeGroups.map((outcomeGroup) => {
        return <OutcomeGroupBox key={outcomeGroup.id} outcomeGroup={outcomeGroup} />;
      }),
    [data],
  );

  return (
    <div className="flex h-full w-full flex-col gap-6 self-stretch overflow-y-auto">
      {error && <div className="bg-red-50 text-red-700 mb-4 rounded-md p-4">{error.message}</div>}

      {isLoading && (
        <div className="mt-8 text-center text-black-tint-40">{t("common.loading")}...</div>
      )}

      {data && (
        <>
          <ProgressOverviewBox
            outcomeGroups={data.outcomeGroups}
            totalAssessed={data.totalOutcomesAssessed}
            totalScore={data.averageOutcomeScore}
          />

          {withOutcomeCards && outcomeCards}
        </>
      )}
    </div>
  );
};
